import * as React from "react";
import { Input, Spinner } from "react-ui";
import { KeyboardEvent } from "react";
import { debounce } from "lodash";

import { FilterExpression, FilterType, OperatorType } from "../../../../Helpers/FilterExpressions/FilterExpression";
import { FILTER_INPUT_TYPE } from "../../../metricsAction";
import { MetricsActionPayload, trackAction } from "../../../Internals/Metrics/MetricsProvider";
import { pullContext } from "../../../TypingUtils";
import { FilterSourceInfoType, MultiLineFilter } from "../../MultiLine/MultiLineFilter";
import { AbsoluteModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { tablePathContextTypes } from "../../CrossfitTable/TablePathContext";
import { Icon } from "../../../Icons/Icon";

import { Root } from "./FilterInput.styles";

interface FilterInputState {
    value?: string;
    isLoading: boolean;
}

export interface FilterInputProps {
    multilinePath: AbsoluteModelPath;
    tableId?: string;
    type: FilterType;
    dataPaths: AbsoluteModelPath[];
    placeholder?: string;
    value: string;
    width?: string | number;
    runSearchOnlyOnEnter?: boolean;
    filteredUniqKey?: string;

    onChangeFilter: (stickyTablePath: string, filter: MultiLineFilter) => void;
    onRemoveFilter: (stickyTablePath: string, filterSourceId: string) => void;
    onBuildCustomFilterExpression?: (value: string) => Promise<FilterExpression | undefined>;
}

interface FilterInputContext {
    stickyTablePath: string;
}

function filterInputSelector(props: FilterInputProps, _: FilterInputState, value: string): MetricsActionPayload {
    return { path: props.dataPaths.map(x => x.toLegacyPath()).join(","), value: value };
}

export class FilterInput extends React.Component<FilterInputProps, FilterInputState> {
    public static contextTypes = tablePathContextTypes;

    private readonly debouncedApplyFilter = debounce((value: string) => this.applyFilter(value), 700);

    public state: FilterInputState = {
        value: undefined,
        isLoading: false,
    };

    public render(): JSX.Element {
        const leftIcon = this.state.isLoading ? <Spinner type="mini" caption={""} /> : <Icon name="SearchLoupeIcon" />;
        return (
            <Root>
                <Input
                    value={this.state.value ?? this.props.value}
                    leftIcon={leftIcon}
                    placeholder={this.props.placeholder}
                    onValueChange={this.handleOnChange}
                    width={this.props.width ?? "100%"}
                    onKeyPress={this.handleOnKeyPress}
                />
            </Root>
        );
    }

    private readonly handleOnKeyPress = async (e: KeyboardEvent): Promise<void> => {
        if (e.key === "Enter" && !this.state.isLoading) {
            await this.applyFilter(this.state.value ?? this.props.value);
        }
    };

    private readonly handleOnChange = async (value: string): Promise<void> => {
        this.setState({ value: value });
        if (!(this.props.runSearchOnlyOnEnter ?? false)) {
            await this.debouncedApplyFilter(value);
        }
    };

    @trackAction(FILTER_INPUT_TYPE, filterInputSelector)
    private async applyFilter(value: string): Promise<void> {
        this.setState({ isLoading: true });
        try {
            const filterExpression =
                this.props.onBuildCustomFilterExpression != undefined
                    ? await this.props.onBuildCustomFilterExpression(value)
                    : this.buildFilterExpression(value);

            const multilineFilter: undefined | MultiLineFilter =
                filterExpression != undefined
                    ? {
                          filterSourceId: this.props.dataPaths.map(x => x.toLegacyPath()).join("_"),
                          expression: filterExpression,
                          filterSourceInfo: {
                              type: FilterSourceInfoType.String,
                              value: value ?? "",
                          },
                      }
                    : undefined;

            if (multilineFilter != undefined) {
                this.props.onChangeFilter(this.getStickyTablePath(), multilineFilter);
            } else {
                this.props.onRemoveFilter(
                    this.getStickyTablePath(),
                    this.props.dataPaths.map(x => x.toLegacyPath()).join("_")
                );
            }
            this.setState({ value: undefined });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    private getStickyTablePath(): string {
        const contextPath = this.getContext().stickyTablePath;
        if (contextPath != undefined) {
            return contextPath;
        }
        return this.props.tableId ?? "";
    }

    private getFilterExpression(value: string): FilterExpression {
        return {
            operator: OperatorType.Or,
            conditions: this.props.dataPaths.map(path => ({
                operator: OperatorType.Substring,
                condition: {
                    dataPath: path.toLegacyPath(),
                    value: value,
                },
            })),
        };
    }

    private buildFilterExpression(value: undefined | string): FilterExpression | undefined {
        if (value === "" || value == undefined) {
            return undefined;
        }
        const valueParts = value.split(" ");
        const filterExpressions: FilterExpression[] = [];

        for (const part of valueParts) {
            filterExpressions.push(this.getFilterExpression(part));
        }
        return {
            operator: OperatorType.And,
            conditions: filterExpressions,
        };
    }

    private getContext(): FilterInputContext {
        return pullContext<FilterInputContext>(this);
    }
}
